/* eslint-disable max-lines */
import { BulkSelector } from '@core/components/BulkSelector';
import type { BulkSelectableItem } from '@core/components/BulkSelector';
import { Button, Slider } from '@soteria/design-system-ve';
import { useState, useRef, useCallback, useEffect } from 'react';

import * as styles from './LabelSelector.css';
import * as sliderInputStyles from './SliderInputStyles.css';

// ===== Configuration Types =====

/**
 * Configuration for severity-based labels (text content)
 * Currently used for: TEXT content type
 */
export interface SeverityConfig {
  mode: 'severity';
  min: number;
  defaultValue: number;
  defaultMax: number;
  showDescription?: boolean;
  getValueLabel: (value: number, max?: number) => string;
}

/**
 * Configuration for score threshold-based labels (image content)
 * Currently used for: IMAGE content type
 */
export interface ScoreConfig {
  mode: 'score';
  min: number;
  max: number;
  defaultValue: number;
  showDescription?: boolean;
  getValueLabel: (value: number) => string;
  getValueDescription?: (value: number) => string;
}

/**
 * Configuration for presence-based labels (no value, just labelId).
 * Used for: moderation image labels, custom labels.
 */
export interface PresenceConfig {
  mode: 'presence';
}

/**
 * EXTENSIBILITY: Future Content Types
 *
 * To add support for new content types (Audio, Video, Username), follow these steps:
 *
 * 1. Create a new config interface (e.g., AudioConfig, VideoConfig, UsernameConfig)
 *    - Add a unique 'mode' discriminator (e.g., mode: 'audio')
 *    - Define min/max/default values appropriate for the content type
 *    - Add getValueLabel and optional getValueDescription functions
 *
 * 2. Create corresponding data interfaces:
 *    - LabelWithAudio, LabelWithVideo, LabelWithUsername
 *    - Include labelId and the value property (e.g., audioThreshold)
 *
 * 3. Create label option interfaces:
 *    - AudioLabelOption, VideoLabelOption, UsernameLabelOption
 *    - Extend BulkSelectableItem with id and labelName
 *
 * 4. Update union types:
 *    - Add new config to ValueConfig union
 *    - Add new label type to Label union
 *    - Add new option type to LabelOption union
 *
 * 5. Add type guards:
 *    - isAudioConfig, isVideoConfig, isUsernameConfig
 *    - isAudioProps, isVideoProps, isUsernameProps
 *
 * 6. Update component logic:
 *    - handleAddLabel: Add case for new content type
 *    - handleBulkAddLabels: Add case for new content type
 *    - handleValueChange: Add case for new content type
 *    - getValueFromLabel: Add case for new content type
 *
 * Example for Audio:
 * ```typescript
 * export interface AudioConfig {
 *   mode: 'audio';
 *   min: number;
 *   max: number;
 *   defaultValue: number;
 *   showDescription?: boolean;
 *   getValueLabel: (value: number) => string;
 *   getValueDescription?: (value: number) => string;
 * }
 *
 * export interface LabelWithAudio {
 *   labelId: string;
 *   audioThreshold: number;
 * }
 * ```
 */

export type ValueConfig = SeverityConfig | ScoreConfig | PresenceConfig;

// ===== Data Types =====

/**
 * Generic label selection — used for all label types.
 * All value fields are optional to support presence-based labels that only need labelId.
 * severity is used for text content; scoreThreshold is used for score-based image content.
 * Presence-based selections (moderation image labels, custom labels) omit both.
 */
export interface Label {
  labelId: string;
  severity?: number;
  scoreThreshold?: number;
}

/**
 * Label option for severity-based selection
 */
export interface SeverityLabelOption extends BulkSelectableItem {
  id: string;
  labelName: string;
  maxSeverity?: number;
}

/**
 * Label option for score-based selection
 */
export interface ScoreLabelOption extends BulkSelectableItem {
  id: string;
  labelName: string;
}

export type LabelOption = SeverityLabelOption | ScoreLabelOption;
// TODO: Add AudioLabelOption | VideoLabelOption | UsernameLabelOption when those content types are implemented

// ===== Utility Functions =====
// TODO: Add getAudioLabel, getVideoLabel, getUsernameLabel when those content types are implemented

/**
 * Get the display field name for a given config mode.
 */
function getFieldName(config: ValueConfig): string {
  if (isPresenceConfig(config)) return 'Presence';
  if (isSeverityConfig(config)) return 'Severity';

  return 'Score Threshold';
  // TODO: Add 'Audio Threshold', 'Video Threshold', 'Username Threshold'
}

/**
 * Get a formatted value display string for a given config and value.
 */
function getValueDisplay(config: ValueConfig, val: number, maxVal?: number): string {
  if (isPresenceConfig(config)) return 'Presence';

  if (isSeverityConfig(config)) {
    return `${config.getValueLabel(val, maxVal)} (${val}/${maxVal})`;
  }

  // ScoreConfig
  return `${config.getValueLabel(val)} (${val})`;
  // TODO: Add cases for audio, video, username
}

/**
 * Extract the numeric value from a label based on config mode.
 */
function getValueFromLabel(config: ValueConfig, label: Label): number {
  if (isPresenceConfig(config)) return 0;

  if (isSeverityConfig(config)) {
    return label.severity ?? config.defaultValue;
  }

  // ScoreConfig
  return label.scoreThreshold ?? config.defaultValue;
  // TODO: Add cases for audio, video, username
}

/**
 * Create a new label with the appropriate value field based on config mode.
 */
function createLabel(config: ValueConfig, labelId: string, val: number): Label {
  if (isPresenceConfig(config)) {
    return { labelId };
  }

  if (isSeverityConfig(config)) {
    return { labelId, severity: val };
  }

  // ScoreConfig
  return { labelId, scoreThreshold: val };
  // TODO: Add cases for audio, video, username
}

/**
 * Convert severity number to human-readable label
 */
export const getSeverityLabel = (severity: number, maxSeverity: number = 3): string => {
  if (maxSeverity <= 3) {
    // For 3-level system: 1=Low, 2=Medium, 3=High
    switch (severity) {
      case 1:
        return 'Low';
      case 2:
        return 'Medium';
      case 3:
        return 'High';
      default:
        return severity.toString();
    }
  } else {
    // For larger scales, just use numbers
    return severity.toString();
  }
};

/**
 * Convert score threshold to human-readable label
 */
export const getScoreLabel = (score: number): string => {
  if (score <= 50) {
    return 'Permissive';
  } else if (score <= 70) {
    return 'Moderate';
  } else {
    return 'Strict';
  }
};

/**
 * Get description for score ranges
 */
export const getScoreDescription = (score: number): string => {
  if (score <= 50) {
    return 'Catches most potential violations (0-50)';
  } else if (score <= 70) {
    return 'Balanced approach (51-70)';
  } else {
    return 'High-confidence violations only (71-100)';
  }
};

/**
 * Validate numeric input and clamp to valid range
 */
const validateAndClamp = (value: string, min: number, max: number): number => {
  const numValue = parseInt(value, 10);

  if (isNaN(numValue)) return min;

  return Math.max(min, Math.min(max, numValue));
};

// ===== Inline Editable Value Component =====

interface InlineEditableValueProps {
  value: number;
  min: number;
  max: number;
  compact?: boolean;
  onChange: (value: number) => void;
  ariaLabel?: string;
}

function InlineEditableValue({ value, min, max, compact = false, onChange, ariaLabel }: InlineEditableValueProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(() => {
    setIsEditing(true);
    setEditValue(value.toString());
  }, [value]);

  const handleBlur = useCallback(() => {
    const numValue = validateAndClamp(editValue, min, max);

    onChange(numValue);
    setIsEditing(false);
  }, [editValue, min, max, onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleBlur();
      } else if (e.key === 'Escape') {
        setEditValue(value.toString());
        setIsEditing(false);
      }
    },
    [handleBlur, value]
  );

  // Auto-focus when switching to edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        aria-label={ariaLabel}
        className={compact ? sliderInputStyles.compactInlineEditInput : sliderInputStyles.inlineEditInput}
        max={max}
        min={min}
        type="number"
        value={editValue}
        onBlur={handleBlur}
        onChange={e => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return (
    <button
      aria-label={ariaLabel || `Click to edit value ${value}`}
      className={compact ? sliderInputStyles.compactInlineEditableValue : sliderInputStyles.inlineEditableValue}
      type="button"
      onClick={handleClick}
    >
      {value}
    </button>
  );
}

// ===== Main Component Props =====

interface BaseLabelSelectorProps {
  loading?: boolean;
  title?: string;
  emptyStateMessage?: string;
  enableBulkSelection?: boolean;
  itemsPerPage?: number;
  readOnly?: boolean;
}

interface SeverityLabelSelectorProps extends BaseLabelSelectorProps {
  config: SeverityConfig;
  availableLabels: SeverityLabelOption[];
  selectedLabels: Label[];
  onChange: (labels: Label[]) => void;
}

interface ScoreLabelSelectorProps extends BaseLabelSelectorProps {
  config: ScoreConfig;
  availableLabels: ScoreLabelOption[];
  selectedLabels: Label[];
  onChange: (labels: Label[]) => void;
}

interface PresenceLabelSelectorProps extends BaseLabelSelectorProps {
  config: PresenceConfig;
  availableLabels: LabelOption[];
  selectedLabels: Label[];
  onChange: (labels: Label[]) => void;
}

// TODO: Add AudioLabelSelectorProps, VideoLabelSelectorProps, UsernameLabelSelectorProps when implementing those content types

export type LabelSelectorProps = SeverityLabelSelectorProps | ScoreLabelSelectorProps | PresenceLabelSelectorProps;
// TODO: Update union to include: | AudioLabelSelectorProps | VideoLabelSelectorProps | UsernameLabelSelectorProps

// ===== Type Guards =====
// TODO: Add isAudioConfig/isAudioProps, isVideoConfig/isVideoProps, isUsernameConfig/isUsernameProps when implementing those content types

function isSeverityConfig(config: ValueConfig): config is SeverityConfig {
  return config.mode === 'severity';
}

function isPresenceConfig(config: ValueConfig): config is PresenceConfig {
  return config.mode === 'presence';
}

// ===== Main Component =====

// eslint-disable-next-line complexity
export function LabelSelector(props: LabelSelectorProps) {
  const {
    config,
    availableLabels,
    selectedLabels,
    loading,
    emptyStateMessage = 'No labels selected. Add labels to define criteria.',
    enableBulkSelection = false,
    itemsPerPage = 16,
    readOnly = false,
    onChange,
  } = props;

  const isPresence = isPresenceConfig(config);

  const [selectedLabelId, setSelectedLabelId] = useState('');
  const [value, setValue] = useState(isPresence ? 0 : config.defaultValue);

  // Get the max value for a label (only relevant for severity mode)
  const getMaxValueForLabel = (labelId: string): number => {
    if (isPresence) return 0;

    if (isSeverityConfig(config)) {
      const label = availableLabels.find(l => l.id === labelId) as SeverityLabelOption;

      return label?.maxSeverity ?? config.defaultMax;
    }

    return config.max;
  };

  // Get the current max value for display
  const currentMaxValue = isPresence ? 0 : selectedLabelId ? getMaxValueForLabel(selectedLabelId) : isSeverityConfig(config) ? config.defaultMax : config.max;

  const unselectedLabels = availableLabels.filter(label => !selectedLabels.some(selected => selected.labelId === label.id));

  // Single label add handler (not used in presence mode)
  const handleAddLabel = () => {
    if (isPresence) return;

    const maxVal = getMaxValueForLabel(selectedLabelId);

    if (selectedLabelId && selectedLabelId.trim() && value >= config.min && value <= maxVal) {
      const clampedValue = isSeverityConfig(config) ? Math.min(value, maxVal) : value;

      onChange([...selectedLabels, createLabel(config, selectedLabelId.trim(), clampedValue)]);
      setSelectedLabelId('');
      setValue(config.defaultValue);
    }
  };

  // Bulk selection toggle handler - adds or removes labels immediately
  const handleBulkToggleLabels = (labelIds: string[]) => {
    const currentSelectedIds = selectedLabels.map(l => l.labelId);
    const addedIds = labelIds.filter(id => !currentSelectedIds.includes(id));
    const removedIds = currentSelectedIds.filter(id => !labelIds.includes(id));

    let updated = removedIds.length > 0 ? selectedLabels.filter(label => !removedIds.includes(label.labelId)) : [...selectedLabels];

    if (addedIds.length > 0) {
      const defaultVal = isPresence ? 0 : config.defaultValue;
      const newLabels: Label[] = addedIds.map(labelId => createLabel(config, labelId, defaultVal));

      updated = [...updated, ...newLabels];
    }

    onChange(updated);
  };

  const handleRemoveLabel = (labelId: string) => {
    onChange(selectedLabels.filter(label => label.labelId !== labelId));
  };

  const handleValueChange = (labelId: string, newValue: number) => {
    if (isPresence) return;

    if (isSeverityConfig(config)) {
      onChange(selectedLabels.map(label => (label.labelId === labelId ? { ...label, severity: newValue } : label)));
    } else {
      onChange(selectedLabels.map(label => (label.labelId === labelId ? { ...label, scoreThreshold: newValue } : label)));
    }
  };

  const getLabelName = (labelId: string) => availableLabels.find(label => label.id === labelId)?.labelName || labelId;

  const fieldName = getFieldName(config);
  const fieldNameLower = fieldName.toLowerCase();

  if (loading) {
    return <div className={styles.loading}>Loading labels...</div>;
  }

  return (
    <div className={styles.container}>
      {!readOnly &&
        availableLabels.length > 0 &&
        (enableBulkSelection ? (
          // Bulk Selection Mode
          <div className={styles.bulkSelectionContainer}>
            <BulkSelector
              className={styles.formField}
              emptyPageMessage="No labels available on this page"
              emptySearchMessage="No labels found matching your search"
              getItemSearchText={item => item.labelName}
              items={availableLabels}
              itemsPerPage={itemsPerPage}
              legend="Select Labels"
              renderItem={item => item.labelName}
              searchAriaLabel="Search labels"
              searchPlaceholder="Search labels..."
              selectedItemIds={selectedLabels.map(l => l.labelId)}
              onSelectedItemsChange={handleBulkToggleLabels}
            />
          </div>
        ) : !isPresence ? (
          // Single Selection Mode (not applicable for presence-based labels)
          <div className={styles.singleSelectionContainer}>
            <div className={styles.formField}>
              <label className={styles.fieldLabel} htmlFor="labelSelect">
                Select Label
              </label>
              <select className={styles.select} id="labelSelect" value={selectedLabelId} onChange={e => setSelectedLabelId(e.target.value)}>
                <option value="">Choose a label...</option>
                {unselectedLabels.map(label => (
                  <option key={label.id} value={label.id}>
                    {label.labelName}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formField}>
              <div className={sliderInputStyles.sliderInputContainer}>
                <div className={sliderInputStyles.verticalSliderContainer}>
                  <div className={sliderInputStyles.sliderLabelHeader}>
                    <span className={sliderInputStyles.sliderLabelText}>
                      {fieldName}: {getValueDisplay(config, value, currentMaxValue)}
                    </span>
                    <InlineEditableValue ariaLabel={`${fieldName} value input`} max={currentMaxValue} min={config.min} value={value} onChange={setValue} />
                  </div>
                  <div className={sliderInputStyles.sliderWrapper}>
                    <Slider
                      showMinMaxLabels
                      aria-describedby={config.showDescription ? `${fieldNameLower}-description` : undefined}
                      color="success"
                      id={`${fieldName.replace(/\s/g, '')}Slider`}
                      label=""
                      max={currentMaxValue}
                      min={config.min}
                      step={1}
                      subLabel={config.mode === 'score' && config.showDescription && config.getValueDescription ? config.getValueDescription(value) : undefined}
                      value={Math.min(value, currentMaxValue)}
                      onChange={setValue}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.formActions}>
              <Button disabled={!selectedLabelId || !selectedLabelId.trim()} onClick={handleAddLabel}>
                Add Label
              </Button>
            </div>
          </div>
        ) : null)}

      {/* Selected Labels List */}
      {selectedLabels.length > 0 && (
        <div>
          <div className={styles.sectionTitle}>
            {props.title || 'Selected Labels'} ({selectedLabels.length})
          </div>
          <div className={styles.labelsList}>
            {selectedLabels.map(label => {
              const labelValue = getValueFromLabel(config, label);
              const labelMaxValue = getMaxValueForLabel(label.labelId);
              const displayValue = Math.min(labelValue, labelMaxValue);

              return (
                <div key={label.labelId} className={styles.labelItem}>
                  <div className={styles.labelInfo}>
                    <div className={styles.labelName}>{getLabelName(label.labelId)}</div>
                    {!isPresence && (
                      <div className={styles.labelValue}>
                        {fieldName}: {getValueDisplay(config, displayValue, labelMaxValue)}
                      </div>
                    )}
                    {!isPresence && config.mode === 'score' && config.showDescription && config.getValueDescription && (
                      <div className={styles.labelValueDesc}>{config.getValueDescription(displayValue)}</div>
                    )}
                  </div>
                  <div className={styles.labelControls}>
                    {!isPresence && (
                      <div className={sliderInputStyles.compactSliderInputRow}>
                        <div className={sliderInputStyles.compactSliderWrapper}>
                          <Slider
                            aria-describedby={config.showDescription ? `${fieldNameLower}-desc-${label.labelId}` : undefined}
                            aria-label={`${fieldName} for ${getLabelName(label.labelId)}: ${getValueDisplay(config, displayValue, labelMaxValue)}`}
                            color="success"
                            disabled={readOnly}
                            max={labelMaxValue}
                            min={config.min}
                            showMinMaxLabels={false}
                            size="small"
                            step={1}
                            value={displayValue}
                            onChange={newValue => handleValueChange(label.labelId, newValue)}
                          />
                        </div>
                        <InlineEditableValue
                          compact
                          ariaLabel={`${fieldName} value input for ${getLabelName(label.labelId)}`}
                          max={labelMaxValue}
                          min={config.min}
                          value={displayValue}
                          onChange={newValue => handleValueChange(label.labelId, newValue)}
                        />
                      </div>
                    )}
                    {!readOnly && (
                      <Button aria-label={`Remove ${getLabelName(label.labelId)} label`} onClick={() => handleRemoveLabel(label.labelId)}>
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedLabels.length === 0 && (
        <div aria-live="polite" className={styles.emptyState} role="status">
          {emptyStateMessage}
        </div>
      )}
    </div>
  );
}
